import { type ReactNode, useCallback, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import {
	FileTooLargeError,
	IMAGE_FILE_INPUT_ACCEPT,
	fileUploadService,
} from '@/api/fileUploadApi.ts';
import { useUserId } from '@/auth/UserIdContext.tsx';
import { useAddImageToLibrary } from '@/hooks/useAddImageToLibrary.ts';
import { centerCropToAspect, type CropAspect } from '@/utils/imageCrop.ts';

import {
	ImageReplaceError,
	type ImageReplaceErrorType,
} from './ImageReplaceError.tsx';

const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png']);

interface UseImageReplaceArgs {
	artifactId: string;
	slideIndex?: number;
	aspect?: CropAspect;
	/**
	 * 'replace' (default) overwrites the slide at slideIndex. 'insert' uploads a
	 * new slide and adds it to the carousel at atIndex via do_add_slide, leaving
	 * existing slides untouched.
	 */
	mode?: 'replace' | 'insert';
	/** Insertion position for 'insert' mode; defaults to appending at the end. */
	atIndex?: number;
	/**
	 * When true (insert mode only), the file picker accepts MULTIPLE files and
	 * each is uploaded + appended as its own slide in one action. Used to fill an
	 * empty carousel in a single pick so it lands directly in the normal filled
	 * view (with its built-in per-slide remove/add) rather than the collapse-prone
	 * one-at-a-time path. No-op in replace mode (a replace targets one slide).
	 */
	allowMultiple?: boolean;
	/**
	 * The deck this hook writes into, for 'replace' mode. Supplying it lets the
	 * hook confirm the slide the customer aimed at is still the slide at
	 * `slideIndex` when the file dialog resolves. Omit it and the index is
	 * trusted as before.
	 */
	slideUrls?: readonly (string | null | undefined)[];
}

interface UseImageReplaceResult {
	openPicker: () => void;
	isUploading: boolean;
	/**
	 * Hidden file input + error overlay. Place inside the image's
	 * `position: relative` container so the error overlay fills it.
	 */
	elements: ReactNode;
}

export const useImageReplace = ({
	artifactId,
	slideIndex,
	aspect = '1:1',
	mode = 'replace',
	atIndex,
	allowMultiple = false,
	slideUrls,
}: UseImageReplaceArgs): UseImageReplaceResult => {
	const bridgeApi = useBridgeApi();
	const userId = useUserId();
	const addImageToLibrary = useAddImageToLibrary(userId);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [error, setError] = useState<ImageReplaceErrorType | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	// The slide the customer aimed at, captured when the dialog opens. The write
	// index is read again when the dialog resolves, which can be minutes later,
	// so the two have to be compared before anything is written.
	const pickerTargetRef = useRef<{
		index: number;
		url: string | null | undefined;
	} | null>(null);

	const snapshotPickerTarget = useCallback(() => {
		pickerTargetRef.current =
			mode === 'replace' && slideIndex !== undefined
				? { index: slideIndex, url: slideUrls?.[slideIndex] }
				: null;
	}, [mode, slideIndex, slideUrls]);

	const validateImageFile = useCallback((file: File): boolean => {
		const ext = `.${file.name.toLowerCase().split('.').pop()}`;
		return IMAGE_EXTENSIONS.has(ext);
	}, []);

	// Validate → transcode HEIC → crop → upload a single picked file. Returns the
	// uploaded blob URL, or null after setting the appropriate error. Shared by
	// the single-file (replace/insert) and multi-file (insert) paths.
	const processOneFile = useCallback(
		async (
			picked: File,
			uid: string,
		): Promise<{ url: string; wasCropped: boolean } | null> => {
			let file: File;
			try {
				file = await fileUploadService.convertHeicToJpeg(picked);
			} catch (conversionError) {
				setError(
					conversionError instanceof FileTooLargeError
						? 'file_too_large'
						: 'invalid_format',
				);
				return null;
			}
			if (!validateImageFile(file)) {
				setError('invalid_format');
				return null;
			}
			const { isValid, reason } = fileUploadService.isValidFile(
				file,
				validateImageFile,
			);
			if (!isValid) {
				setError(reason === 'size' ? 'file_too_large' : 'invalid_format');
				return null;
			}
			const { blob, wasCropped } = await centerCropToAspect(file, aspect);
			const croppedFile = new File([blob], file.name, { type: blob.type });
			const result = await fileUploadService.upload(croppedFile, uid);
			addImageToLibrary(result.url, 'post');
			return { url: result.url, wasCropped };
		},
		[addImageToLibrary, aspect, validateImageFile],
	);

	const handleFileSelect = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const picked = Array.from(event.target.files ?? []);
			if (picked.length === 0 || !userId) return;
			const uploadChatId = bridgeApi.user_state?.current_chat_id ?? undefined;

			if (event.target) {
				event.target.value = '';
			}

			// Multi-file insert: upload every picked photo and append each as its own
			// slide. Used to fill an empty carousel in one pick so it lands directly
			// in the normal filled view. Each do_add_slide appends at the end.
			if (mode === 'insert' && allowMultiple) {
				setIsUploading(true);
				try {
					for (const one of picked) {
						const uploaded = await processOneFile(one, userId);
						if (!uploaded) continue; // error surfaced; keep any that succeed
						bridgeApi.sendAction({
							type: 'do_add_slide',
							artifact_id: artifactId,
							image_url: uploaded.url,
							base_image_url: uploaded.url,
						});
					}
				} finally {
					setIsUploading(false);
				}
				return;
			}

			// A redraw that lands while the dialog is open splices the restored slide
			// into image_urls (insertRedrawnSlideIntoContent in
			// bridge/actions/helpers.ts), so the deck grows under a
			// selected_slide_index that nothing moves. The index the customer aimed
			// at then names a different picture, and writing to it would overwrite a
			// slide they never touched while their own slide kept its old photo. The
			// pick is refused instead, before any upload or outstanding request.
			const aimedAt = pickerTargetRef.current;
			pickerTargetRef.current = null;
			if (
				mode === 'replace' &&
				aimedAt !== null &&
				slideUrls !== undefined &&
				slideUrls[aimedAt.index] !== aimedAt.url
			) {
				setError('slide_moved');
				return;
			}

			const requestId = crypto.randomUUID();
			// 'insert' adds a brand-new slide, so there is no existing slide to mark
			// as being replaced — only the replace path registers the outstanding
			// request that report_post_image_replaced later clears.
			if (mode === 'replace') {
				bridgeApi.sendAction(
					{
						type: 'request_replace_post_image',
						artifact_id: artifactId,
						...(slideIndex !== undefined && { image_index: slideIndex }),
					},
					requestId,
				);
			}

			setIsUploading(true);
			try {
				const uploaded = await processOneFile(picked[0], userId);
				if (uploaded === null) {
					// processOneFile already set the error; unwind the replace request.
					if (mode === 'replace') {
						bridgeApi.sendAction(
							{
								type: 'report_post_image_replace_failed',
								artifact_id: artifactId,
								chat_id: uploadChatId,
							},
							requestId,
						);
					}
					return;
				}
				if (mode === 'insert') {
					// An uploaded slide is its own logo-free base (no baked-in mark), so
					// the bridge treats image_url as base_image_url too.
					bridgeApi.sendAction({
						type: 'do_add_slide',
						artifact_id: artifactId,
						image_url: uploaded.url,
						base_image_url: uploaded.url,
						...(atIndex !== undefined && { at_index: atIndex }),
					});
				} else {
					bridgeApi.sendAction(
						{
							type: 'report_post_image_replaced',
							artifact_id: artifactId,
							image_url: uploaded.url,
							was_cropped: uploaded.wasCropped,
							chat_id: uploadChatId,
							...(slideIndex !== undefined && { image_index: slideIndex }),
						},
						requestId,
					);
				}
			} catch (err) {
				console.error('Image replace failed:', err);
				if (mode === 'replace') {
					bridgeApi.sendAction(
						{
							type: 'report_post_image_replace_failed',
							artifact_id: artifactId,
							chat_id: uploadChatId,
						},
						requestId,
					);
				}
				setError('upload_failure');
			} finally {
				setIsUploading(false);
			}
		},
		[
			allowMultiple,
			artifactId,
			atIndex,
			bridgeApi,
			mode,
			processOneFile,
			slideIndex,
			slideUrls,
			userId,
		],
	);

	const openPicker = useCallback(() => {
		snapshotPickerTarget();
		fileInputRef.current?.click();
	}, [snapshotPickerTarget]);

	const handleTryAgain = useCallback(() => {
		setError(null);
		// Re-aim at the deck as it stands now, so a retry after a slide moved
		// targets what the customer can currently see.
		snapshotPickerTarget();
		fileInputRef.current?.click();
	}, [snapshotPickerTarget]);

	const handleCancel = useCallback(() => {
		setError(null);
	}, []);

	const elements = (
		<>
			<input
				ref={fileInputRef}
				type="file"
				accept={IMAGE_FILE_INPUT_ACCEPT}
				multiple={mode === 'insert' && allowMultiple}
				onChange={handleFileSelect}
				className="hidden"
			/>
			{error && (
				<ImageReplaceError
					errorType={error}
					onTryAgain={handleTryAgain}
					onCancel={handleCancel}
				/>
			)}
		</>
	);

	return { openPicker, isUploading, elements };
};
